import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { GameCard } from "@/components/GameCard";
// `ActivityFeed` was removed from the dashboard per Dominik's request — the
// admin-sync events that landed there ("API-Football Sync: 0 neu, 72 aktualisiert")
// aren't user-facing content. The component itself stays in the codebase in
// case we want a curated user-facing version later (real "goal scored" /
// "ranking shake-up" events instead of internal sync noise).
import { Countdown } from "@/components/Countdown";
import { RacePodium } from "@/components/RacePodium";
import { isLocked, lockTime, formatKickoff } from "@/lib/time";
import { GROUP_PRIZES, PRIZE_DEADLINE, WM_GROUPS, PHASE_LABELS } from "@/lib/constants";
import { loadTopRanking, loadGroupRankings, type GroupRankingRow } from "@/lib/ranking";
import { SignupBonusBanner } from "@/components/SignupBonusBanner";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { InviteButton } from "@/components/InviteButton";
import { MyGroupRankings } from "@/components/MyGroupRankings";
import { OnboardingTour } from "@/components/OnboardingTour";
import { BookOpen } from "lucide-react";
import { BonusBanner } from "@/components/BonusBanner";
import { SinglePrizeBanner } from "@/components/SinglePrizeBanner";
import { StreakBadge } from "@/components/StreakBadge";
import { useLiveBonus } from "@/hooks/useLiveBonus";
import { teamDe } from "@/lib/teamNames";
import { useTournamentFinal } from "@/hooks/useTournamentFinal";
import { ChampionsSpotlight } from "@/components/ChampionsSpotlight";
import { SeasonWrapUp } from "@/components/SeasonWrapUp";

type Game = any;
type Tip = any;

const TABS = [
  { key: "upcoming", label: "Kommend" },
  { key: "results", label: "Ergebnisse" },
  { key: "groups", label: "Gruppen A–L" },
  { key: "ko", label: "KO-Phase" },
  { key: "mine", label: "Meine Tipps" },
] as const;

export default function Dashboard() {
  const { user, profile } = useAuth();
  const [games, setGames] = useState<Game[]>([]);
  const [tips, setTips] = useState<Record<string, Tip>>({});
  const [rank, setRank] = useState<number | null>(null);
  const [totalPoints, setTotalPoints] = useState(0);
  const [totalTippers, setTotalTippers] = useState(0);
  const [groupRanks, setGroupRanks] = useState<GroupRankingRow[]>([]);
  const [tab, setTab] = useState<typeof TABS[number]["key"]>("upcoming");
  const [tabTouched, setTabTouched] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const liveBonus = useLiveBonus();
  const myLiveBonus = user ? (liveBonus.perUser[user.id] ?? 0) : 0;
  // Tournament is over once the bonus questions are resolved — see the hook.
  const { final, truth } = useTournamentFinal();

  // Once it's decided, "Kommend" is permanently empty — land on the results
  // archive instead. Only until the user picks a tab themselves.
  useEffect(() => {
    if (final && !tabTouched) setTab("results");
  }, [final, tabTouched]);

  useEffect(() => {
    // Auto-fire the in-app tour exactly once: after the signup wizard
    // finishes (onboardingCompleted=true) but before tourSeen is set.
    // The wizard itself sets onboardingCompleted, the tour completion
    // handler below sets tourSeen — so the two flags must be distinct.
    if (profile && profile.onboardingCompleted && !profile.tourSeen) {
      // small delay so layout is ready before measuring targets
      const t = setTimeout(() => setShowTour(true), 400);
      return () => clearTimeout(t);
    }
  }, [profile]);

  // OnboardingTour itself persists tourSeen=true via the profile API
  // and calls refreshProfile() before invoking onFinish — so we just
  // need to hide the overlay locally here.
  const finishTour = () => setShowTour(false);

  useEffect(() => {
    document.title = "Englputzeder Tippspiel – WM 2026";
    let mounted = true;
    (async () => {
      const [g, t, topRanking, gRanks] = await Promise.all([
        api.games.list(),
        user ? (api.tips.listMine() as Promise<any[]>) : Promise.resolve([] as any[]),
        // limit:0 → only own rank + total; the Dashboard never renders the
        // ranking list itself, so downloading it was pure payload waste.
        loadTopRanking(0),
        loadGroupRankings(),
      ]);
      if (!mounted) return;
      // The API returns camelCase. Map to the snake_case shape this page (and
      // the GameCard component) was built against — simpler than touching every
      // grandchild render path.
      const gamesNormalized = (g as any[]).map((x) => ({
        ...x,
        kickoff_time: x.kickoffTime,
        home_team: x.homeTeam,
        away_team: x.awayTeam,
        home_flag: x.homeFlag,
        away_flag: x.awayFlag,
        home_score: x.homeScore,
        away_score: x.awayScore,
        live_home_score: x.liveHomeScore,
        live_away_score: x.liveAwayScore,
        is_finished: x.isFinished,
        is_live: x.isLive,
        group_label: x.groupLabel,
        status_short: x.statusShort,
        last_live_sync: x.lastLiveSync,
      }));
      // Drop phantom cross-group fixtures (group-phase games the sync
      // labelled "?" because their two teams belong to different groups —
      // invalid data that would otherwise show as a tippable "Gruppe ?"
      // game). Test ("TEST") and import ("EXT") games are unaffected.
      const cleanGames = gamesNormalized.filter(
        (g: any) => !(g.phase === "group" && g.group_label === "?"),
      );
      setGames(cleanGames);
      const byGame: Record<string, Tip> = {};
      (t as any[]).forEach((x: any) => { byGame[x.gameId] = { ...x, game_id: x.gameId, home_tip: x.homeTip, away_tip: x.awayTip, points_earned: x.pointsEarned, xp_earned: x.xpEarned }; });
      setTips(byGame);
      setGroupRanks(gRanks);
      setTotalTippers(topRanking.total);
      if (user && topRanking.me) {
        setRank(topRanking.me.rank);
        setTotalPoints(topRanking.me.points);
      }
    })();
    return () => { mounted = false; };
  }, [user]);

  const nextGame = useMemo(() => {
    const now = Date.now();
    return games.find(g => new Date(g.kickoff_time).getTime() + 110*60_000 > now) ?? null;
  }, [games]);

  // "Has the WM actually started yet?" — true once any REAL WM fixture's
  // kickoff is in the past. Excludes ad-hoc imports (groupLabel="EXT")
  // and manual test games (gameNo >= 9000), same invariant the bonus
  // lock uses. Drives the hero copy: pre-tournament shows a countdown
  // pitch, post-kickoff shows "läuft!".
  const wmHasStarted = useMemo(() => {
    const now = Date.now();
    return games.some((g) =>
      typeof g.group_label === "string" &&
      /^[A-L]$/.test(g.group_label) &&
      new Date(g.kickoff_time).getTime() <= now,
    );
  }, [games]);

  const filtered = useMemo(() => {
    const now = Date.now();
    if (tab === "upcoming") {
      return games.filter(g => new Date(g.kickoff_time).getTime() + 110*60_000 > now).slice(0, 12);
    }
    if (tab === "results") {
      // Finished games, most-recent first. Cap at 30 so old groups don't
      // dominate the view once the WM is rolling — users wanting the full
      // archive can switch to Gruppen A–L / KO-Phase.
      return games
        .filter(g => g.is_finished && g.home_score != null)
        .sort((a, b) => new Date(b.kickoff_time).getTime() - new Date(a.kickoff_time).getTime())
        .slice(0, 30);
    }
    if (tab === "groups") return games.filter(g => g.phase === "group");
    if (tab === "ko") return games.filter(g => g.phase !== "group");
    if (tab === "mine") return games.filter(g => tips[g.id]);
    return games;
  }, [games, tab, tips]);

  const grouped = useMemo(() => {
    if (tab === "groups") {
      const by: Record<string, Game[]> = {};
      filtered.forEach(g => {
        const k = g.group_label || "?";
        (by[k] = by[k] ?? []).push(g);
      });
      return WM_GROUPS.map(k => ({ key: k, label: `Gruppe ${k}`, games: by[k] ?? [] })).filter(x => x.games.length > 0);
    }
    if (tab === "ko") {
      const by: Record<string, Game[]> = {};
      filtered.forEach(g => {
        const k = g.phase;
        (by[k] = by[k] ?? []).push(g);
      });
      return Object.keys(by).map(k => ({ key: k, label: PHASE_LABELS[k] ?? k, games: by[k] }));
    }
    return null;
  }, [filtered, tab]);

  // ───────── End of tournament ─────────
  // Everything below the spotlight that assumes an ongoing Tippspiel (next
  // game + countdown, "Jetzt tippen", the group race with its award timer,
  // prize teasers, invite CTA, bonus-question banner, today's-matchday share)
  // is either meaningless or actively misleading once the winners are fixed.
  // The results archive and the rules link stay.
  if (final) {
    return (
      <div className="space-y-6">
        <ChampionsSpotlight truth={truth} currentUserId={user?.id ?? null} />

        {user && (
          <SeasonWrapUp
            games={games}
            tips={tips}
            rank={rank}
            totalTippers={totalTippers}
            totalPoints={totalPoints}
            bestStreak={profile?.bestStreak ?? 0}
            username={profile?.username}
          />
        )}

        {/* Tabs (results archive) */}
        <div className="bg-card rounded-pill p-1 flex shadow-card overflow-x-auto">
          {TABS.filter((t) => t.key !== "upcoming").map(t => (
            <button key={t.key} onClick={()=>{ setTabTouched(true); setTab(t.key); }} className={`flex-1 min-w-[80px] text-xs font-bold py-2 rounded-pill tap transition ${tab === t.key ? "bg-accent text-accent-foreground" : "text-muted-foreground"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {grouped ? (
          <div className="space-y-5">
            {grouped.map(grp => (
              <section key={grp.key}>
                <h2 className="font-display font-bold text-base mb-2 px-1">{grp.label}</h2>
                <div className="space-y-2">
                  {grp.games.map((g: any) => <GameCard key={g.id} game={g} tip={tips[g.id]} />)}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.length === 0 && (
              <div className="bg-card rounded-lg shadow-card p-8 text-center text-sm text-muted-foreground">
                {tab === "mine" ? "Du hast keine Tipps abgegeben." : "Keine Spiele."}
              </div>
            )}
            {filtered.map(g => <GameCard key={g.id} game={g} tip={tips[g.id]} />)}
          </div>
        )}

        <Link to="/regeln" className="block bg-card rounded-lg shadow-card p-3 flex items-center gap-3 tap hover:bg-muted transition">
          <BookOpen className="h-5 w-5 text-accent-foreground bg-accent rounded-md p-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold leading-tight">Wie wurde gewertet?</div>
            <div className="text-[11px] text-muted-foreground">Punktesystem, Bonus-Tipps & alle Spielregeln nachlesen</div>
          </div>
          <span className="text-xs text-muted-foreground">→</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero */}
      <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} className="bg-hero text-primary-foreground rounded-lg shadow-pop p-5 relative overflow-hidden">
        <div className="absolute -right-6 -top-6 text-[120px] opacity-10">🏆</div>
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs font-bold uppercase tracking-wider text-primary-foreground/80">Hi {profile?.emoji} {profile?.username}</div>
          {profile && (
            <StreakBadge
              streak={profile.streak ?? 0}
              bestStreak={profile.bestStreak ?? 0}
              lastTipDate={profile.lastTipDate ?? null}
              freezeUsedAt={profile.streakFreezeUsedAt ?? null}
            />
          )}
        </div>
        <h1 className="font-display font-extrabold text-2xl mt-1 leading-tight">
          Bereit zum Tippen?<br/>
          <span className="text-primary-foreground underline decoration-2 decoration-primary-foreground/50 underline-offset-4">
            {wmHasStarted ? "WM 2026 läuft!" : "WM 2026 startet bald!"}
          </span>
        </h1>
        {nextGame && (
          <div data-tour="next-game" className="mt-3 bg-primary-foreground/10 backdrop-blur rounded-md p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-primary-foreground/80 flex items-center gap-1.5">
              {nextGame.is_live ? (
                <>
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                  Läuft jetzt {nextGame.minute ? `· ${nextGame.minute}'` : ""}
                </>
              ) : "Nächstes Spiel"}
            </div>
            <div className="flex items-center justify-between mt-1 gap-2">
              <div className="text-sm font-semibold flex-1 min-w-0 truncate">
                {nextGame.home_flag} {teamDe(nextGame.home_team)} <span className="opacity-50">vs</span> {teamDe(nextGame.away_team)} {nextGame.away_flag}
              </div>
              {nextGame.is_live && nextGame.live_home_score != null && nextGame.live_away_score != null && (
                <div className="font-display font-extrabold text-lg tabular-nums shrink-0">
                  {nextGame.live_home_score}:{nextGame.live_away_score}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="text-[11px] opacity-80">{formatKickoff(nextGame.kickoff_time)}</div>
              {nextGame.is_live ? (
                tips[nextGame.id] ? (
                  <span className={`text-[11px] font-bold rounded-pill px-2 py-0.5 ${myLiveBonus > 0 || (liveBonus.perUser[user?.id ?? ""] ?? 0) > 0 ? "bg-gold text-foreground" : "bg-primary-foreground/20"}`}>
                    Dein Tipp: {tips[nextGame.id].home_tip}:{tips[nextGame.id].away_tip}
                  </span>
                ) : (
                  <span className="text-[11px] font-bold opacity-80">Kein Tipp 😬</span>
                )
              ) : !isLocked(nextGame.kickoff_time) ? (
                <Countdown target={lockTime(nextGame.kickoff_time)} compact />
              ) : (
                <span className="text-xs font-bold text-primary-foreground">Tipps gesperrt 🔒</span>
              )}
            </div>
            <Link to={`/spiel/${nextGame.id}`} className="mt-3 block text-center bg-primary-foreground text-primary rounded-md py-2.5 font-bold text-sm tap">
              {nextGame.is_live ? "Live anschauen 📺" : "Jetzt tippen! 🎯"}
            </Link>
          </div>
        )}
      </motion.div>

      {/* Race um Gruppen-Sponsoring */}
      <div className="bg-card rounded-lg shadow-card p-4 space-y-3">
        <RacePodium />
        {user && <MyGroupRankings userId={user.id} />}
      </div>

      {/* Einzel-Hauptpreis */}
      <SinglePrizeBanner variant="hero" />

      {/* Anmelde-Bonus + Einladung */}
      <SignupBonusBanner variant="card" />

      <div className="bg-card rounded-lg shadow-card p-3 flex items-center gap-3">
        <div className="text-2xl">📲</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold leading-tight">Mit Freunden tippen?</div>
          <div className="text-[11px] text-muted-foreground">Je mehr Tipper in deiner Gruppe, desto höher die Chance aufs Sponsoring.</div>
        </div>
        <InviteButton variant="icon" />
      </div>

      {/* Bonus-Fragen prominent */}
      <BonusBanner variant="compact" />

      {/* Spieltag-Karte (Share) */}
      <SpieltagCardLink games={games} tips={tips} />


      {/* Quick stats */}
      <div data-tour="stats" className="grid grid-cols-2 gap-2">
        <Stat label="Punkte" value={totalPoints} accent liveBonus={myLiveBonus} />
        <Stat label="Rang" value={rank ? `#${rank}` : "—"} />
      </div>

      {/* Tabs */}
      <div data-tour="tabs" className="bg-card rounded-pill p-1 flex shadow-card overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={()=>{ setTabTouched(true); setTab(t.key); }} className={`flex-1 min-w-[80px] text-xs font-bold py-2 rounded-pill tap transition ${tab === t.key ? "bg-accent text-accent-foreground" : "text-muted-foreground"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Games */}
      {grouped ? (
        <div className="space-y-5">
          {grouped.map(grp => (
            <section key={grp.key}>
              <h2 className="font-display font-bold text-base mb-2 px-1">{grp.label}</h2>
              <div className="space-y-2">
                {grp.games.map((g: any) => <GameCard key={g.id} game={g} tip={tips[g.id]} />)}
              </div>
            </section>
          ))}
          {grouped.length === 0 && (
            <div className="bg-card rounded-lg shadow-card p-8 text-center text-sm text-muted-foreground">Noch keine Spiele in dieser Phase.</div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.length === 0 && (
            <div className="bg-card rounded-lg shadow-card p-8 text-center text-sm text-muted-foreground">
              {tab === "mine"
                ? "Du hast noch keine Tipps abgegeben."
                : tab === "results"
                ? "Noch keine Spiele beendet."
                : "Keine Spiele."}
            </div>
          )}
          {filtered.map(g => <GameCard key={g.id} game={g} tip={tips[g.id]} />)}
        </div>
      )}

      {/* Regeln link */}
      <Link to="/regeln" className="block bg-card rounded-lg shadow-card p-3 flex items-center gap-3 tap hover:bg-muted transition">
        <BookOpen className="h-5 w-5 text-accent-foreground bg-accent rounded-md p-1 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold leading-tight">Wie funktioniert's?</div>
          <div className="text-[11px] text-muted-foreground">Punktesystem, Bonus-Tipps & alle Spielregeln nachlesen</div>
        </div>
        <span className="text-xs text-muted-foreground">→</span>
      </Link>

      {showTour && <OnboardingTour onFinish={finishTour} />}
    </div>
  );
}

function Stat({ label, value, accent, liveBonus }: { label: string; value: any; accent?: boolean; liveBonus?: number }) {
  return (
    <div className={`rounded-lg p-3 shadow-card text-center ${accent ? "bg-accent text-accent-foreground" : "bg-card"}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-display font-extrabold text-xl tabular-nums flex items-center justify-center gap-1.5">
        {value}
        {liveBonus !== undefined && liveBonus > 0 && (
          <span className="text-[10px] font-bold bg-gold text-foreground rounded-pill px-1.5 py-0.5 animate-pulse">
            +{liveBonus} ✨ live
          </span>
        )}
      </div>
    </div>
  );
}

function SpieltagCardLink({ games, tips }: { games: any[]; tips: Record<string, any> }) {
  const today = new Date().toISOString().slice(0, 10);
  const todays = games.filter(g => g.kickoff_time?.slice(0, 10) === today);
  if (todays.length === 0) return null;
  const tipped = todays.filter(g => tips[g.id]).length;
  return (
    <Link
      to={`/spieltag/${today}`}
      className="block bg-card rounded-lg shadow-card p-3 flex items-center gap-3 tap hover:bg-muted transition"
    >
      <div className="text-2xl">📸</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold leading-tight">Mein Spieltag teilen</div>
        <div className="text-[11px] text-muted-foreground">
          {todays.length} Spiel{todays.length === 1 ? "" : "e"} heute · {tipped} getippt → als Story posten
        </div>
      </div>
      <span className="text-xs text-muted-foreground">→</span>
    </Link>
  );
}

function GroupPrizeStatusLine({ groupRanks, userId }: { groupRanks: GroupRankingRow[]; userId: string | null }) {
  const [myGroupIds, setMyGroupIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!userId) { setMyGroupIds(new Set()); return; }
    api.groups.mine().then((groups: any[]) => {
      setMyGroupIds(new Set(groups.map((g: any) => g.id)));
    });
  }, [userId]);

  if (!userId) {
    return <div className="text-center text-xs text-muted-foreground px-1">Anmelden, tippen und mit deiner Gruppe gewinnen! ⚽</div>;
  }
  if (myGroupIds.size === 0) {
    return (
      <Link to="/gruppen" className="block rounded-md bg-accent/15 border border-accent/30 p-2.5 text-center text-xs font-bold text-foreground tap">
        👥 Tritt einer Tippgruppe bei und kämpft gemeinsam um 5.000 € Sponsoring →
      </Link>
    );
  }
  // Beste Platzierung der Gruppen des Users
  const myGroups = groupRanks.filter(g => myGroupIds.has(g.group_id));
  if (myGroups.length === 0) return null;
  const best = myGroups.reduce((a, b) => (a.rank <= b.rank ? a : b));
  if (best.rank <= 3) {
    const prize = GROUP_PRIZES[best.rank - 1];
    return (
      <div className={`rounded-md p-2.5 text-center font-bold text-sm ${prize.tone === "gold" ? "bg-gold" : prize.tone === "silver" ? "bg-silver" : "bg-bronze"}`}>
        {prize.icon} <b>{best.name}</b> liegt auf Rang {best.rank} → aktuell <span className="tabular-nums">{prize.short}</span> Sponsoring
      </div>
    );
  }
  const third = groupRanks.find(g => g.rank === 3);
  const diff = third ? Math.max(1, third.total_points - best.total_points + 1) : null;
  return (
    <div className="text-center text-xs text-muted-foreground px-1">
      <b className="text-foreground">{best.name}</b> ist auf Rang #{best.rank} — noch{" "}
      <b className="text-foreground tabular-nums">{diff ?? "?"}</b> Punkte bis zu den Preisrängen 🎁
    </div>
  );
}
